import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { COLORS, formatYAxisNumber } from '../utils/ChartConstants';
import { ChartData, NewChartData, DataPointChartData } from '../utils/ChartInterfaces';
import { ChartTypeDetector } from '../utils/ChartTypeDetector';

interface ComboChartProps {
  data: any[];
  originalChart?: ChartData;
}

// Custom tick component that wraps text or tilts based on width
const CustomWrappedTick = (props: any) => {
  const { x, y, payload, width, shouldTilt } = props;

  if (!payload?.value) return null;

  const text = String(payload.value);

  // If shouldTilt is true, tilt all labels with wrapping
  if (shouldTilt) {
    const fontSize = 11;
    const maxCharsPerLine = 20; // Maximum characters per line for tilted text

    // Split text into lines
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);

    // Limit to 2-3 lines for tilted text
    const displayLines = lines.slice(0, 3);

    return (
      <text
        x={x}
        y={y}
        textAnchor="end"
        fill="#666"
        fontSize={fontSize}
        transform={`rotate(-45 ${x} ${y})`}
      >
        {displayLines.map((line, index) => (
          <tspan
            key={index}
            x={x}
            dy={index === 0 ? '0.71em' : '1.1em'}
            dx="-0.5em"
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  }

  // Otherwise, wrap text within point width
  const pointWidth = width || 60;
  const fontSize = 12;
  const charWidth = fontSize * 0.55;
  const maxCharsPerLine = Math.floor((pointWidth - 4) / charWidth);

  // Function to break word with hyphen if needed
  const breakWord = (word: string, maxLength: number): string[] => {
    if (word.length <= maxLength) return [word];

    const parts: string[] = [];
    let remaining = word;

    while (remaining.length > 0) {
      if (remaining.length <= maxLength) {
        parts.push(remaining);
        break;
      }
      const breakPoint = maxLength - 1;
      parts.push(remaining.substring(0, breakPoint) + '-');
      remaining = remaining.substring(breakPoint);
    }

    return parts;
  };

  // Process text into lines
  const lines: string[] = [];
  const words = text.split(' ');
  let currentLine = '';

  for (const word of words) {
    if (word.length > maxCharsPerLine) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = '';
      }
      const brokenWords = breakWord(word, maxCharsPerLine);
      lines.push(...brokenWords);
    } else {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
  }
  if (currentLine) lines.push(currentLine);

  // Limit to max 3 lines
  const displayLines = lines.slice(0, 3);
  if (lines.length > 3) {
    const lastLine = displayLines[2];
    if (lastLine.length > maxCharsPerLine - 3) {
      displayLines[2] = lastLine.substring(0, maxCharsPerLine - 3) + '...';
    } else {
      displayLines[2] = lastLine + '...';
    }
  }

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill="#666"
      fontSize={fontSize}
    >
      {displayLines.map((line, index) => (
        <tspan
          key={index}
          x={x}
          dy={index === 0 ? '0.71em' : '1.2em'}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
};

// Custom bar shape with rounded top corners
const RoundedBar = (props: any) => {
  const { fill, x, y, width, height } = props;
  const radius = 8;

  // Only round the top corners
  const path = `
    M ${x},${y + height}
    L ${x},${y + radius}
    Q ${x},${y} ${x + radius},${y}
    L ${x + width - radius},${y}
    Q ${x + width},${y} ${x + width},${y + radius}
    L ${x + width},${y + height}
    Z
  `;

  return <path d={path} fill={fill} />;
};

// Custom tooltip for ComboChart
const ComboChartTooltip = (props: any) => {
  const { active, payload, label } = props;

  if (active && payload && payload.length) {
    return (
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '18px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          minWidth: '180px'
        }}
      >
        {/* Values above separator */}
        {payload.map((entry: any, index: number) => (
          <div
            key={index}
            style={{
              margin: '0',
              color: entry.color || '#333',
              fontWeight: 400,
              paddingBottom: index === payload.length - 1 ? '8px' : '4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '16px'
            }}
          >
            <span>{entry.name || entry.dataKey}</span>
            <span style={{ fontWeight: 600 }}>{formatYAxisNumber(entry.value)}</span>
          </div>
        ))}

        {/* Separator */}
        <div
          style={{
            borderTop: '1px solid #e0e0e0',
            margin: '8px 0',
          }}
        />

        {/* X-axis value at bottom */}
        <p style={{ margin: 0, fontWeight: 600, color: '#333', paddingTop: '4px' }}>
          {label}
        </p>
      </div>
    );
  }

  return null;
};

export const ComboChart: React.FC<ComboChartProps> = ({ data, originalChart }) => {
  // Calculate approximate point spacing based on data count
  const estimatedPointWidth = Math.max(30, 300 / data.length);

  // Check if any label would overflow when wrapped
  const shouldTiltLabels = React.useMemo(() => {
    // Always tilt if we have more than 3 data points or point width is less than 100px
    if (data.length > 3 || estimatedPointWidth < 100) return true;

    const fontSize = 12;
    const charWidth = fontSize * 0.55;
    const maxCharsPerLine = Math.floor((estimatedPointWidth - 4) / charWidth);

    return data.some(item => {
      if (!item.name) return false;
      const text = String(item.name);

      // Check if text is too long overall or any word is too long
      if (text.length > maxCharsPerLine * 2) return true;

      const words = text.split(' ');
      return words.some(word => word.length > maxCharsPerLine);
    });
  }, [data, estimatedPointWidth]);
  // Determine how to render the combo chart based on the original structure
  if (originalChart && ChartTypeDetector.isNewStructure(originalChart)) {
    const newChart = originalChart as NewChartData;

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <defs>
            {COLORS.map((color, index) => (
              <linearGradient key={`gradient-${index}`} id={`comboBarGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                <stop offset="100%" stopColor={color} stopOpacity={0.3} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            tick={<CustomWrappedTick width={estimatedPointWidth} shouldTilt={shouldTiltLabels} />}
            interval={0}
            height={80}
          />
          <YAxis yAxisId="y" tickFormatter={formatYAxisNumber} />
          <YAxis yAxisId="y1" orientation="right" tickFormatter={formatYAxisNumber} />
          <Tooltip content={<ComboChartTooltip />} />
          <Legend />
          {newChart.datasets.map((dataset, index) => {
            const color = COLORS[index % COLORS.length];
            if (dataset.type === 'line') {
              return (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={dataset.label || 'value'}
                  stroke={color}
                  yAxisId={dataset.yAxisID || 'y'}
                  strokeWidth={2}
                />
              );
            } else if (dataset.type === 'area') {
              return (
                <Area
                  key={index}
                  type="monotone"
                  dataKey={dataset.label || 'value'}
                  stroke={color}
                  fill={color}
                  fillOpacity={0.3}
                  yAxisId={dataset.yAxisID || 'y'}
                />
              );
            } else {
              return (
                <Bar
                  key={index}
                  dataKey={dataset.label || 'value'}
                  fill={`url(#comboBarGradient-${index % COLORS.length})`}
                  yAxisId={dataset.yAxisID || 'y'}
                  shape={RoundedBar}
                />
              );
            }
          })}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  // Handle DataPointChartData structure
  if (originalChart && ChartTypeDetector.isDataPointStructure(originalChart)) {
    const dpChart = originalChart as DataPointChartData;
    const config = dpChart.chart_config;
    
    const allKeys = config?.series ||
      (data.length > 0 ? Object.keys(data[0]).filter(k => k !== 'name' && k !== 'label') : []);

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <defs>
            {COLORS.map((color, index) => (
              <linearGradient key={`gradient-${index}`} id={`comboBarGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.8} />
                <stop offset="100%" stopColor={color} stopOpacity={0.3} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            tick={<CustomWrappedTick width={estimatedPointWidth} shouldTilt={shouldTiltLabels} />}
            interval={0}
            height={80}
          />
          <YAxis tickFormatter={formatYAxisNumber} />
          <Tooltip content={<ComboChartTooltip />} />
          <Legend />
          {allKeys.map((key, i) => {
            const t = config?.series_types?.[key] || 'bar';
            const color = COLORS[i % COLORS.length];
            if (t === 'line') {
              return <Line key={key} type="monotone" dataKey={key} stroke={color} strokeWidth={2} />;
            } else if (t === 'area') {
              return <Area key={key} type="monotone" dataKey={key} stroke={color} fill={color} fillOpacity={0.3} />;
            } else {
              return <Bar key={key} dataKey={key} fill={`url(#comboBarGradient-${i % COLORS.length})`} shape={RoundedBar} />;
            }
          })}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  // Fallback for simple data
  const dataKeys = data.length > 0 ? Object.keys(data[0]).filter(k => k !== 'name' && typeof data[0][k] === 'number') : [];
  
  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <defs>
          {COLORS.map((color, index) => (
            <linearGradient key={`gradient-${index}`} id={`comboBarGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.8} />
              <stop offset="100%" stopColor={color} stopOpacity={0.3} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="name"
          tick={<CustomWrappedTick width={estimatedPointWidth} shouldTilt={shouldTiltLabels} />}
          interval={0}
          height={80}
        />
        <YAxis tickFormatter={formatYAxisNumber} />
        <Tooltip content={<ComboChartTooltip />} />
        {dataKeys.length > 1 && <Legend />}
        {dataKeys.map((key, i) => (
          <Bar key={key} dataKey={key} fill={`url(#comboBarGradient-${i % COLORS.length})`} shape={RoundedBar} />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
};